import { VodItemViewProps } from '@/types/vod/VodItem'
import { Box, Button, Typography, useTheme } from '@mui/material'
import React, { useEffect } from 'react'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { VodItemLabel } from '@/features/VOD/components/VodItemLabel'
import { useVODStore } from '@/shared/store'
import placeholderPoster from '@/shared/assets/images/placeholderPoster.webp'
import { useVodItemViewLogic } from '@/features/VOD/hooks/useVodItemViewLogic'
import VodItemWrapper from '@/features/VOD/components/vodItem/VodItemWrapper'
import FilmOverviewInfos from '@/features/VOD/components/itemDetails/components/FilmOverviewInfos'
import ReadMore from '@/components/tv/components/guideView/programGuideView/components/ReadMore'
import { useTranslation } from 'react-i18next'
import { isSmartTV } from '@/shared/lib'

export const ZeroRowItemView = ({
    item,
    handleGoToCategoryButtonClick,
    scrollPosition,
    cardRadiusDp,
    isHomeContentItem,
    isActive,
    focused,
}: VodItemViewProps) => {
    const { handleItemClick, details, imageUrl, title, label } =
        useVodItemViewLogic(item, handleGoToCategoryButtonClick)
    const { t: translate } = useTranslation()
    const theme = useTheme()
    const { setIsLinkedFromHome } = useVODStore()

    const itemBoxStyle = {
        scrollSnapAlign: 'end',
        position: 'relative',
        '&:hover': {
            cursor: 'pointer',
        },
    }
    const gradientCover = {
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        background:
            'linear-gradient(0deg, rgba(39,39,39,1) 10%, rgba(0,212,255,0) 70%)',
    }
    const vodContentStyle = {
        borderRadius: cardRadiusDp
            ? `${cardRadiusDp}px`
            : theme.shape.borderRadius,
        overflow: 'hidden',
    }

    const labelStyle = {
        position: 'absolute',
        top: '-20px',
        right: '0px',
    }

    const imageStyle = {
        position: 'relative',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: '16px',
        gap: '16px',
    }

    const hoverImageStyle = {
        position: 'absolute',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        bottom: '64px',
    }

    const contentContainer = {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        width: '50%',
        height: '100%',
        marginTop: { xs: '32px', sm: 0 },
        paddingLeft: '16px',
    }

    const { duration, genres, year, country } = details || {}

    const handleClick = () => {
        handleItemClick('')
        isHomeContentItem && setIsLinkedFromHome(true)
    }

    useEffect(() => {
        if (!isActive || !focused) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                handleClick()
            }
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [isActive, item.title, focused])

    return (
        <VodItemWrapper
            isHomeContentItem={isHomeContentItem}
            assetType={item.assetType}
            productId={item.productId}>
            <>
                <Box sx={itemBoxStyle} onClick={handleClick}>
                    <Box sx={vodContentStyle}>
                        <Box sx={imageStyle}>
                            <Box
                                sx={{
                                    height: { sx: '50vh', lg: '60vh' },
                                }}>
                                <LazyLoadImage
                                    alt={item.title || 'movie poster'}
                                    src={imageUrl}
                                    width="100%"
                                    height="100%"
                                    style={{
                                        objectFit: 'cover',
                                    }}
                                    scrollPosition={scrollPosition}
                                    placeholderSrc={placeholderPoster}
                                    onError={(event) => {
                                        const target =
                                            event.target as HTMLImageElement
                                        target.onerror = null
                                        target.src = placeholderPoster
                                    }}
                                />
                                <Box sx={gradientCover} />
                            </Box>
                            <Box sx={hoverImageStyle}>
                                <Box sx={contentContainer}>
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        position="relative"
                                        zIndex={1}
                                        pb="44px">
                                        <Typography variant="headline1">
                                            {title}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <FilmOverviewInfos
                                            year={year}
                                            genres={genres}
                                            country={country}
                                            duration={duration}
                                        />
                                        {details && (
                                            <Box position="relative">
                                                <ReadMore
                                                    maxTextLength={550}
                                                    text={
                                                        details.description ??
                                                        ''
                                                    }
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                            {!isSmartTV() && (
                                <Button
                                    onClick={handleClick}
                                    sx={{
                                        marginLeft: '16px',
                                        position: 'relative',
                                        height: '36px',
                                        width: '80px',
                                        color: 'white',
                                        padding: '8px 12px',
                                        backgroundColor: 'secondary.main',
                                        borderRadius: theme.shape.borderRadius,
                                        cursor: 'pointer',
                                    }}>
                                    {translate('details')}
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>

                {label && (
                    <Box sx={labelStyle}>
                        <VodItemLabel label={label} />
                    </Box>
                )}
            </>
        </VodItemWrapper>
    )
}
